import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { Button } from './ui';

/**
 * Dialogs.
 *
 * Radix supplies the behaviour that is easy to get subtly wrong by hand: focus
 * moves in on open and back to the trigger on close, Tab cannot escape, Escape
 * closes, and the rest of the page is hidden from assistive technology while it
 * is up. The styling is ours.
 */

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="animate-pop fixed inset-0 z-50 bg-overlay backdrop-blur-[1px]" />
        <RadixDialog.Content
          className={cn(
            'animate-pop fixed top-[12vh] left-1/2 z-50 flex max-h-[76vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col',
            'rounded-panel border border-line bg-raised shadow-float',
            widths[size],
          )}
        >
          <div className="flex items-start gap-3 border-b border-line px-4 py-3">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title dir="auto" className="text-reading font-semibold text-ink">
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description dir="auto" className="mt-0.5 text-mini text-ink-muted">
                  {description}
                </RadixDialog.Description>
              ) : (
                // Radix warns without a description; an explicitly empty one
                // says "there is nothing more to read" to assistive technology.
                <RadixDialog.Description className="sr-only-focusable" />
              )}
            </div>
            <RadixDialog.Close asChild>
              <Button size="icon" variant="ghost" aria-label="Close" className="-me-1.5 -mt-1 size-7">
                <X aria-hidden className="size-4" />
              </Button>
            </RadixDialog.Close>
          </div>
          {children && <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>}
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">{footer}</div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  /** The verb on the button. "Delete", "Deactivate" — never "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'default';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * `window.confirm`, but one that looks like the rest of the application, names
 * the action on its button, and can be read in Arabic as well as English.
 *
 * The browser's own dialog says "OK" and "Cancel" to a question like "Remove
 * this category?", and "OK" is an answer to a different question. It also
 * blocks the whole tab and cannot be styled, themed, or tested. This keeps the
 * one property worth keeping — an awaitable yes or no — so a call site changes
 * from `if (window.confirm(text))` to `if (await confirm({...}))`.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    // A second request while one is open answers the first with "no" rather
    // than leaving its promise pending forever.
    resolver.current?.(false);
    setOptions(next);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOptions(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={options !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
        title={options?.title ?? ''}
        description={options?.description}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={options?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
              // The safe choice is not the focused one for a destructive
              // action: Radix focuses the first focusable element, which is
              // the dialog's close button, so Enter does not delete anything.
            >
              {options?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used inside a ConfirmProvider');
  return context;
}
